export interface Location {
    latitude: number;
    longitude: number;
    accuracy?: number;
}

export interface GeocodedZipcodes {
    [idx: string]: Location;
}

export namespace Geolocation {
    const defaultGeolocationOptions = {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
    } as PositionOptions;

    const noGeolocationApiError = {
        code: 2,
        message: 'no gelocation API',
    } as GeolocationPositionError;

    const isErrorWithMessage = (error: unknown): error is { message: string } =>
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string';

    const isGeolocationPositionError = (
        error: unknown,
    ): error is GeolocationPositionError =>
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'number' &&
        'message' in error &&
        typeof error.message === 'string';

    const toGeolocationPositionError = (
        error: unknown,
    ): GeolocationPositionError => {
        if (isGeolocationPositionError(error)) {
            return error;
        }
        if (isErrorWithMessage(error)) {
            return {
                code: 2,
                message: error.message,
            } as GeolocationPositionError;
        }
        return {
            code: 2,
            message: 'unknown geolocation error',
        } as GeolocationPositionError;
    };

    const reportError = (
        errorCallback: PositionErrorCallback | undefined,
        error: unknown,
    ) => {
        if (!errorCallback) {
            return;
        }
        setTimeout(() => {
            errorCallback(toGeolocationPositionError(error));
        }, 0);
    };

    export const hasGeolocation = () => 'geolocation' in navigator;

    export const clearWatch = (id: number) =>
        navigator.geolocation.clearWatch(id);

    export const watchPosition = (
        success: PositionCallback = _pos => null,
        errorCallback?: PositionErrorCallback,
    ) => {
        if (!navigator.geolocation || !navigator.geolocation.watchPosition) {
            reportError(errorCallback, noGeolocationApiError);
            return -1;
        }
        try {
            return navigator.geolocation.watchPosition(
                pos => success(pos),
                err => {
                    if (errorCallback) {
                        errorCallback(err);
                    }
                },
                defaultGeolocationOptions,
            );
        } catch (error) {
            reportError(errorCallback, error);
            return -1;
        }
    };

    export const getCurrentPosition = (
        success: PositionCallback = _pos => null,
        errorCallback?: PositionErrorCallback,
    ) => {
        if (
            !navigator.geolocation ||
            !navigator.geolocation.getCurrentPosition
        ) {
            reportError(errorCallback, noGeolocationApiError);
            return -1;
        }
        try {
            navigator.geolocation.getCurrentPosition(
                pos => success(pos),
                err => {
                    if (errorCallback) {
                        errorCallback(err);
                    }
                },
                defaultGeolocationOptions,
            );
        } catch (error) {
            reportError(errorCallback, error);
            return -1;
        }
    };

    const deg2rad = (v: number): number => (v * Math.PI) / 180;

    // calculates distance using equirectangular projection
    // s. http://www.movable-type.co.uk/scripts/latlong.html
    // R is earth radius in m
    export const distance = (a: Location, b: Location) => {
        const R = 6371e3;
        const lat1 = deg2rad(a.latitude);
        const lat2 = deg2rad(b.latitude);
        const long1 = deg2rad(a.longitude);
        const long2 = deg2rad(b.longitude);
        const y = lat2 - lat1;
        const x = (long2 - long1) * Math.cos((lat1 + lat2) / 2);
        return Math.sqrt(x * x + y * y) * R;
    };
}
